// CUSTOM: personalised-v2-card.js
// Card state manager for the v2 personalised library.
//
// Loaded with defer on page.personalised-library-v2.liquid, before personalised-library-v2.js.
// This ensures window.personalised.cardManager is defined when plv2:ready fires.
//
// Exposes: window.personalised.cardManager — { setCardState, resetCard, resolveState, applyAllStates }
// Fires:   plv2:item-added   — { productId, variantId }
// Fires:   plv2:item-removed — { productId, variantId }
//
// Note: removeItem in the spec refers to bundle.removeItemByVariantId(variantId) — that is the
// actual bundle API method. There is no bundle.removeItem().

(function () {
  'use strict';

  window.personalised = window.personalised || {};

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  /**
   * Find a rendered card element by product ID.
   * Searches the entire document — cards may live in any mode container.
   *
   * @param {string} productId
   * @returns {Element|null}
   */
  function _getCard(productId) {
    if (!productId) return null;
    return document.querySelector('.plv2-card[data-product-id="' + productId + '"]');
  }

  /**
   * Update the hero badge element on a card.
   *
   * @param {Element} card
   * @param {string} state  — 'reco' | 'in-pack' | 'sort-goal' | 'default'
   * @param {Object} stateData — contextual data for the state
   */
  function _updateBadge(card, state, stateData) {
    var badge = card.querySelector('.plv2-card__badge');
    if (!badge) return;

    badge.textContent = '';

    if (state === 'reco') {
      badge.textContent = 'Recommended for you';
      badge.removeAttribute('hidden');
    } else if (state === 'in-pack') {
      // In-pack state is communicated by the CTA area — badge stays hidden
      badge.setAttribute('hidden', '');
    } else if (state === 'sort-goal') {
      // Goal filter is already visible in the UI — badge is redundant here
      badge.setAttribute('hidden', '');
    } else {
      // default — badge hidden
      badge.setAttribute('hidden', '');
    }
  }

  /**
   * Update the description paragraph on a card.
   * Falls back to short_description (stored in data-default-description attribute) when
   * state-specific text is unavailable.
   *
   * @param {Element} card
   * @param {string} state
   * @param {Object} stateData
   */
  function _updateDescription(card, state, stateData) {
    var desc = card.querySelector('.plv2-card__description');
    if (!desc) return;

    var defaultText = desc.dataset.defaultDescription || '';

    if (state === 'sort-goal') {
      // Use goal-specific description from custom.goal_descriptions metafield if available,
      // else fall back to the product's short_description.
      desc.textContent = (stateData && stateData.goalDescription) ? stateData.goalDescription : defaultText;
    } else {
      // default, reco, in-pack — always show the product's short_description
      desc.textContent = defaultText;
    }
  }

  /**
   * Reorder the icon row on a card based on state.
   *
   * For 'reco': move the user's quiz goal icons to the front.
   *   Quiz goals are read from window.personalised.libraryV2.state.quizGoals (set by future PR).
   *   Gracefully skips reordering if data is not present.
   *
   * For 'sort-goal': move the selected goal icon to front, add --active class.
   *
   * For 'default' / 'in-pack': restore natural DOM order using data-goal-handle,
   *   remove any --active class.
   *
   * @param {Element} card
   * @param {string} state
   * @param {Object} stateData
   * @param {string} productId
   */
  function _updateIcons(card, state, stateData, productId) {
    var iconRow = card.querySelector('.plv2-card__icons');
    if (!iconRow) return;

    // Remove active highlights from all icons first
    var allIcons = iconRow.querySelectorAll('.plv2-card__icon');
    allIcons.forEach(function (icon) {
      icon.classList.remove('plv2-card__icon--active');
    });

    if (state === 'sort-goal' && stateData && stateData.goalHandle) {
      // Move the matching goal icon to the front; highlight it
      var targetIcon = iconRow.querySelector('.plv2-card__icon[data-goal-handle="' + stateData.goalHandle + '"]');
      if (targetIcon && targetIcon !== iconRow.firstElementChild) {
        iconRow.insertBefore(targetIcon, iconRow.firstElementChild);
      }
      if (targetIcon) {
        targetIcon.classList.add('plv2-card__icon--active');
      }

    } else if (state === 'reco') {
      // Move the user's quiz goal icons to the front if available in page state
      var quizGoals = (window.personalised.libraryV2 && window.personalised.libraryV2.state &&
        window.personalised.libraryV2.state.quizGoals) || [];

      if (quizGoals.length > 0) {
        // Move matching icons to the front in reverse order to preserve goal priority
        for (var i = quizGoals.length - 1; i >= 0; i--) {
          var goalHandle = quizGoals[i];
          var matchIcon = iconRow.querySelector('.plv2-card__icon[data-goal-handle="' + goalHandle + '"]');
          if (matchIcon) {
            iconRow.insertBefore(matchIcon, iconRow.firstElementChild);
          }
        }
      }

    }
    // default / in-pack: no reordering needed — leave as rendered by Liquid

    // Apply goal/quality icon cap after any reordering
    _applyIconCap(iconRow);
  }

  /**
   * Cap visible icons to however many fit in the container width.
   * Each icon is 20px wide with a 4px gap between them.
   * Must run AFTER reordering so the active goal is already at position 0.
   *
   * @param {Element} iconRow — .plv2-card__icons element
   */
  function _applyIconCap(iconRow) {
    var allIcons = Array.prototype.slice.call(iconRow.querySelectorAll('.plv2-card__icon'));

    // Reset any previous cap so we re-evaluate from scratch
    allIcons.forEach(function (el) { el.classList.remove('plv2-card__icon--capped'); });

    var containerWidth = iconRow.offsetWidth;
    if (!containerWidth) { return; }

    var isMobile = window.innerWidth < 768;
    var iconSize = isMobile ? 18 : 20; // px — matches CSS .plv2-card__icon img width
    var gap      = 4;  // px — matches CSS .plv2-card__icons gap

    // How many full icons fit: first icon = iconSize, each subsequent = iconSize + gap
    var maxIcons = containerWidth < iconSize ? 0 : 1 + Math.floor((containerWidth - iconSize) / (iconSize + gap));

    allIcons.slice(maxIcons).forEach(function (el) { el.classList.add('plv2-card__icon--capped'); });
  }

  /**
   * Toggle action buttons based on state.
   * - Non-in-pack states: show add btn, hide in-pack actions
   * - in-pack state: hide add btn, show in-pack actions
   *
   * @param {Element} card
   * @param {string} state
   */
  function _updateActions(card, state, instant) {
    var addBtn    = card.querySelector('.plv2-card__add-btn');
    var removeBtn = card.querySelector('.plv2-card__remove-btn');

    if (state === 'in-pack') {
      if (addBtn) {
        var inPackLabel = addBtn.dataset.inPackLabel || 'Added \u2713';
        if (instant) {
          // Render-time: apply synchronously with no animation to avoid green flash
          addBtn.textContent = inPackLabel;
          addBtn.classList.add('plv2-card__add-btn--in-pack');
          addBtn.setAttribute('disabled', '');
        } else {
          // Live user interaction: cross-fade the button label
          // opacity out → swap content → opacity back in.
          addBtn.style.opacity = '0';
          var _addBtnRef = addBtn;
          setTimeout(function () {
            _addBtnRef.textContent = inPackLabel;
            _addBtnRef.classList.add('plv2-card__add-btn--in-pack');
            _addBtnRef.setAttribute('disabled', '');
            _addBtnRef.style.opacity = ''; // clear inline → CSS transition fades back to 1
          }, 120);
        }
      }
      if (removeBtn) {
        // Remove the display:none (hidden attr), then let the opacity transition fire
        // via a double-rAF so the browser commits the display change first.
        removeBtn.removeAttribute('hidden');
        if (!instant) {
          requestAnimationFrame(function () {
            requestAnimationFrame(function () {
              removeBtn.classList.add('plv2-card__remove-btn--visible');
            });
          });
        } else {
          removeBtn.classList.add('plv2-card__remove-btn--visible');
        }
      }
    } else {
      if (addBtn) {
        // Snap back to default state — cross-fade only needed going IN to in-pack.
        // Clear any in-progress opacity style from a rapid state change.
        addBtn.style.opacity = '';
        addBtn.classList.remove('plv2-card__add-btn--in-pack');
        if (card.dataset.available !== 'false') {
          addBtn.textContent = addBtn.dataset.addLabel || '+ Add to pack';
          addBtn.removeAttribute('disabled');
        }
      }
      if (removeBtn) {
        // Fade the remove button out, then restore display:none after the transition.
        removeBtn.classList.remove('plv2-card__remove-btn--visible');
        var _removeBtnRef = removeBtn;
        setTimeout(function () {
          if (!_removeBtnRef.classList.contains('plv2-card__remove-btn--visible')) {
            _removeBtnRef.setAttribute('hidden', '');
          }
        }, 230); // slightly longer than the 0.2s opacity transition
      }
    }
  }

  /**
   * Apply the state modifier class to the card article element.
   *
   * @param {Element} card
   * @param {string} state
   */
  function _applyStateClass(card, state) {
    card.classList.remove(
      'plv2-card--reco',
      'plv2-card--in-pack',
      'plv2-card--sort-goal'
    );
    if (state !== 'default') {
      card.classList.add('plv2-card--' + state);
    }
    card.dataset.cardState = state;
  }

  // ---------------------------------------------------------------------------
  // Add-to-pack flow
  // ---------------------------------------------------------------------------

  /**
   * Actually add the product to the bundle.
   * Called either directly or after disclaimer confirmation.
   *
   * @param {string} productId
   * @param {string} variantId
   * @param {Element} btn  — the add button (to re-enable on failure)
   */
  function _proceedWithAdd(productId, variantId, btn) {
    var bundle = window.personalised && window.personalised.bundle;
    if (!bundle) {
      console.warn('[CardManager] bundle not available — cannot add item');
      if (btn) { btn.removeAttribute('disabled'); }
      return;
    }

    // Resolve selling plan using the current bundle frequency (matches v1 bundle-manager pattern)
    var currentFrequency = (bundle.getFrequency && bundle.getFrequency()) || '30';
    var config = window.personalised.config;
    var sellingPlanLabel = (config &&
      config.subscription &&
      config.subscription.DEFAULT_SELLING_PLAN_LABELS &&
      config.subscription.DEFAULT_SELLING_PLAN_LABELS[currentFrequency]) || '30_day_subscription';

    var normalizedVariantId = (window.personalised.utils &&
      window.personalised.utils.normalizeShopifyId)
      ? window.personalised.utils.normalizeShopifyId(variantId)
      : variantId;

    var sellingPlanId = (window.personalised.sellingPlans &&
      window.personalised.sellingPlans[normalizedVariantId] &&
      window.personalised.sellingPlans[normalizedVariantId][sellingPlanLabel]) || null;

    if (!sellingPlanId) {
      console.warn('[CardManager] Selling plan not found for variant ' + variantId +
        ' / frequency ' + currentFrequency);
    }

    var added = bundle.addItem({
      productId: productId,
      variantId: variantId,
      collectionId: (window.personalised && window.personalised.config && window.personalised.config.collectionId) || null,
      quantity: 1,
      sellingPlanId: sellingPlanId
    });

    if (added) {
      var addedCard = btn.closest('.plv2-card');
      if (addedCard) {
        cardManager.setCardStateOnElement(addedCard, 'in-pack', { origin: 'user-added' }, productId);
      }
      document.dispatchEvent(new CustomEvent('plv2:item-added', {
        bubbles: true,
        detail: { productId: productId, variantId: variantId }
      }));
    } else {
      console.warn('[CardManager] addItem returned false for product ' + productId);
      if (btn) { btn.removeAttribute('disabled'); }
    }
  }

  /**
   * Handle click on a .plv2-card__add-btn element.
   *
   * @param {Element} btn
   */
  function _handleAddClick(btn) {
    var card = btn.closest('.plv2-card');
    if (!card) return;

    var productId = btn.dataset.productId;
    var variantId = btn.dataset.variantId;

    if (!productId || !variantId) {
      console.warn('[CardManager] Add btn missing product/variant IDs');
      return;
    }

    // Normalize IDs for bundle checks
    var utils = window.personalised && window.personalised.utils;
    var normProductId = utils ? utils.normalizeShopifyId(productId) : productId;
    var normVariantId = utils ? utils.normalizeShopifyId(variantId) : variantId;

    // Guard: already in bundle? (matches v1 bundle-manager pattern)
    var bundle = window.personalised && window.personalised.bundle;
    if (bundle) {
      var alreadyIn = bundle.isInBundle(normVariantId) ||
        (typeof bundle.isProductInBundle === 'function' && bundle.isProductInBundle(normProductId));
      if (alreadyIn) {
        cardManager.setCardStateOnElement(card, 'in-pack', { origin: 'user-added' }, productId);
        return;
      }
    }

    // Disable button immediately to prevent double-click
    btn.setAttribute('disabled', '');

    // Allergy / pregnancy disclaimer check (mirrors v1 bundle-manager pattern)
    var allergy = null;
    var pregnancy = null;
    try {
      allergy = localStorage.getItem('vitable_allergy');
      pregnancy = localStorage.getItem('vitable_pregnancy');
    } catch (e) {
      // localStorage unavailable — proceed without disclaimer
    }

    var hasRelevantAllergy = allergy && !['no', 'none', ''].includes(allergy.toLowerCase().trim());
    var hasRelevantPregnancy = pregnancy && !['no', 'none', ''].includes(pregnancy.toLowerCase().trim());

    if (hasRelevantAllergy || hasRelevantPregnancy) {
      try {
        if (!sessionStorage.getItem('personalised_disclaimer_modal_shown')) {
          var showModal = window.personalised &&
            window.personalised.disclaimerModal &&
            window.personalised.disclaimerModal.showDisclaimerModal;

          if (typeof showModal === 'function') {
            showModal({ allergy: allergy, pregnancy: pregnancy }).then(function (confirmed) {
              if (confirmed) {
                try { sessionStorage.setItem('personalised_disclaimer_modal_shown', 'true'); } catch (e) {}
                _proceedWithAdd(productId, variantId, btn);
              } else {
                btn.removeAttribute('disabled');
              }
            });
            return;
          } else {
            console.warn('[CardManager] Disclaimer modal unavailable — proceeding without modal');
          }
        }
      } catch (e) { /* sessionStorage unavailable, proceed */ }
    }

    // Label modal for users without disclaimer conditions (first add per session)
    if (!(hasRelevantAllergy || hasRelevantPregnancy)) {
      try {
        if (!sessionStorage.getItem('personalised_label_modal_shown')) {
          var showLabelModal = window.personalised &&
            window.personalised.labelModal &&
            window.personalised.labelModal.showLabelModal;
          if (typeof showLabelModal === 'function') {
            showLabelModal().then(function () {
              try {
                sessionStorage.setItem('personalised_label_modal_shown', 'true');
              } catch (e) { /* sessionStorage unavailable */ }
              _proceedWithAdd(productId, variantId, btn);
            });
            return;
          }
        }
      } catch (e) { /* sessionStorage check failed, proceed */ }
    }

    _proceedWithAdd(productId, variantId, btn);
  }

  /**
   * Handle click on a .plv2-card__remove-btn element.
   *
   * @param {Element} btn
   */
  function _handleRemoveClick(btn) {
    var card = btn.closest('.plv2-card');
    if (!card) return;

    var productId = btn.dataset.productId;
    var variantId = btn.dataset.variantId;

    if (!productId || !variantId) {
      console.warn('[CardManager] Remove btn missing product/variant IDs');
      return;
    }

    var bundle = window.personalised && window.personalised.bundle;
    if (!bundle) {
      console.warn('[CardManager] bundle not available — cannot remove item');
      return;
    }

    // Use the correct bundle API: removeItemByVariantId (spec incorrectly says removeItem)
    bundle.removeItemByVariantId(variantId);

    // Revert to default state — subsequent PRs (PR-3,4,5) may re-apply mode-specific state
    // by listening to the plv2:item-removed event
    cardManager.resetCard(productId);

    document.dispatchEvent(new CustomEvent('plv2:item-removed', {
      bubbles: true,
      detail: { productId: productId, variantId: variantId }
    }));
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  var cardManager = {

    /**
     * Set the visual state of a card.
     *
     * @param {string} productId
     * @param {'reco'|'in-pack'|'sort-goal'|'default'} state
     * @param {Object} [stateData]
     *   For 'reco':      { reason?: string }
     *   For 'in-pack':   { origin: 'recommended'|'user-added' }
     *   For 'sort-goal': { goalHandle: string, goalDisplayName: string, goalDescription?: string }
     */
    setCardState: function (productId, state, stateData) {
      stateData = stateData || {};
      var card = _getCard(productId);
      if (!card) return;

      _applyStateClass(card, state);
      _updateBadge(card, state, stateData);
      _updateDescription(card, state, stateData);
      _updateIcons(card, state, stateData, productId);
      _updateActions(card, state);
    },

    /**
     * Reset a card to its default state.
     *
     * @param {string} productId
     */
    resetCard: function (productId) {
      this.setCardState(productId, 'default');
    },

    /**
     * Apply the state priority cascade and return the winning state name.
     * D-5: In-pack > Reco > Sort (goal) > Default
     *
     * @param {string} productId   — for logging only
     * @param {Object} context
     * @param {boolean} context.isInPack
     * @param {boolean} context.isReco
     * @param {string|null} context.activeGoal  — goal handle, or null
     * @returns {'in-pack'|'reco'|'sort-goal'|'default'}
     */
    resolveState: function (productId, context) {
      context = context || {};
      if (context.isInPack) return 'in-pack';
      if (context.isReco) return 'reco';
      if (context.activeGoal) return 'sort-goal';
      return 'default';
    },

    /**
     * Apply a card state directly to a known card element reference.
     *
     * Use this instead of setCardState() when the card element is already
     * in scope (e.g. from _cloneCard()) to avoid the global querySelector
     * returning the wrong card when multiple mode containers share cards for
     * the same product in the DOM.
     *
     * D-27 extension: fixes Modes 1 and 2 DOM-collision (same fix pattern
     * already applied to Mode 3 in D-27).
     *
     * @param {Element} cardEl    — the exact card element to update
     * @param {'reco'|'in-pack'|'sort-goal'|'default'} state
     * @param {Object}  [stateData]
     * @param {string}  [productId]  — required for icon reordering in _updateIcons
     * @param {boolean} [instant]    — skip animations; use during grid renders to avoid flash
     */
    setCardStateOnElement: function (cardEl, state, stateData, productId, instant) {
      if (!cardEl) return;
      stateData = stateData || {};
      _applyStateClass(cardEl, state);
      _updateBadge(cardEl, state, stateData);
      _updateDescription(cardEl, state, stateData);
      _updateIcons(cardEl, state, stateData, productId);
      _updateActions(cardEl, state, instant);
    },

    /**
     * Batch-apply states to all cards visible in the current DOM.
     * Called on mode switch by subsequent PR modules.
     *
     * @param {Map<string, { state: string, stateData: Object }>} stateMap
     *   Key: productId (string), Value: { state, stateData }
     */
    applyAllStates: function (stateMap) {
      if (!stateMap) return;
      stateMap.forEach(function (entry, productId) {
        cardManager.setCardState(productId, entry.state || 'default', entry.stateData || {});
      });
    },

    /**
     * Run _applyIconCap on every .plv2-card__icons element within a container.
     * Call this after grid rendering once layout has settled.
     *
     * @param {Element} container — typically the grid element
     */
    capAllIcons: function (container) {
      var iconRows = container.querySelectorAll('.plv2-card__icons');
      iconRows.forEach(function (row) { _applyIconCap(row); });
    }

  };

  window.personalised.cardManager = cardManager;

  // ---------------------------------------------------------------------------
  // Event delegation — add/remove clicks
  // ---------------------------------------------------------------------------

  // Attached at DOMContentLoaded. All defer scripts have run by then (storage-manager,
  // config, bundle-state, etc.) so window.personalised.bundle is available.
  // Event delegation covers cards rendered by any subsequent PR (PR-3, PR-4, PR-5)
  // since those cards won't exist at DOMContentLoaded time.

  document.addEventListener('DOMContentLoaded', function () {
    document.addEventListener('click', function (e) {
      // Add to pack
      var addBtn = e.target.closest && e.target.closest('.plv2-card__add-btn');
      if (addBtn && !addBtn.disabled) {
        _handleAddClick(addBtn);
        return;
      }

      // Remove from pack
      var removeBtn = e.target.closest && e.target.closest('.plv2-card__remove-btn');
      if (removeBtn) {
        _handleRemoveClick(removeBtn);
        return;
      }

      // Navigate to product page from top-row or description
      var topRow = e.target.closest && e.target.closest('.plv2-card__top-row');
      var desc = e.target.closest && e.target.closest('.plv2-card__description');
      if (topRow || desc) {
        var card = (topRow || desc).closest('.plv2-card');
        if (card) {
          var handle = card.getAttribute('data-product-handle');
          if (handle) { window.location.href = '/products/' + handle; }
        }
      }
    });

    // Recalculate icon caps on resize (debounced)
    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        var grid = document.querySelector('.plv2-grid');
        if (grid) { cardManager.capAllIcons(grid); }
      }, 150);
    });
  });

}());
